import type { StatusMessageProps } from '@/@studio-ai/props';
import { Button, Spinner } from '@heroui/react';
import { Icon } from '@iconify/react';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useId, useRef, useState } from 'react';
import { formatElapsed } from '../utils/answer-timing';
import { buildMarkdownComponents, Markdown } from './markdown';

/** The timer appears once a wait is noticeable, so quick answers stay uncluttered. */
const ELAPSED_SHOWN_AFTER_MS = 5_000;
/** Past this, the user is told they can do something else while DANI works. */
const LONG_WAIT_MS = 60_000;

/** Milliseconds since this status line appeared, updated every second. */
function useElapsedMs(enabled: boolean): number {
    const startedAt = useRef(Date.now());
    const [elapsed, setElapsed] = useState(0);
    useEffect(() => {
        if (!enabled) return;
        const id = setInterval(() => setElapsed(Date.now() - startedAt.current), 1_000);
        return () => clearInterval(id);
    }, [enabled]);
    return elapsed;
}

// `Markdown`'s default p/li/ul/ol renderers hardcode `text-medium` on each
// element (see markdown.tsx) — a wrapping `text-mini` class on this panel
// can't override that from a descendant selector (Tailwind JIT doesn't
// reliably pick up arbitrary `[&_p]:text-*` combos), so the thinking trace
// needs its own component overrides sized directly at the element level.
// Built once at module scope so `Markdown`'s reference-equality memo isn't
// broken by a fresh object on every render.
const thinkingMarkdownComponents = buildMarkdownComponents('text-mini');

function AnimatedDots() {
    return (
        <span className="inline-flex" aria-hidden>
            {[0, 1, 2].map((i) => (
                <motion.span
                    key={i}
                    animate={{ opacity: [0.2, 1, 0.2] }}
                    transition={{
                        duration: 1.4,
                        repeat: Infinity,
                        delay: i * 0.22,
                        ease: 'easeInOut',
                    }}
                >
                    .
                </motion.span>
            ))}
        </span>
    );
}

export const StatusMessage = ({
    message,
    thinkingText,
    isInternal,
    userEcho,
    showElapsed = false,
    className = '',
}: StatusMessageProps) => {
    const thinkingPanelId = useId();
    const elapsedMs = useElapsedMs(showElapsed);
    const [showThinking, setShowThinking] = useState(isInternal);
    const thinkingScrollRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = thinkingScrollRef.current;
        if (el && showThinking) {
            el.scrollTop = el.scrollHeight;
        }
    }, [thinkingText, showThinking]);

    return (
        <motion.div
            className={`w-full ${className}`}
            initial={{ y: 5, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.2 } }}
        >
            <div className="flex items-start gap-3 text-medium text-foreground/80">
                <div className="relative mt-0.5 flex flex-shrink-0 items-center justify-center">
                    <Spinner size="sm" color="success" classNames={{ wrapper: 'h-8 w-8' }} />
                    <Icon
                        icon="lucide:bot"
                        width={16}
                        height={16}
                        className="absolute text-default-600"
                    />
                </div>
                <div className="flex min-w-0 flex-col gap-0.5">
                    <span className="font-medium">
                        {message}
                        <AnimatedDots />
                        {showElapsed && elapsedMs >= ELAPSED_SHOWN_AFTER_MS && (
                            <span
                                className="ml-2 text-small font-normal tabular-nums text-default-400"
                                aria-label={`Working for ${formatElapsed(elapsedMs)}`}
                            >
                                {formatElapsed(elapsedMs)}
                            </span>
                        )}
                    </span>
                    {userEcho && (
                        <motion.span
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.3 }}
                            className="truncate text-small italic text-default-400"
                        >
                            &ldquo;{userEcho}&rdquo;
                        </motion.span>
                    )}
                    {showElapsed && elapsedMs >= LONG_WAIT_MS && (
                        <motion.span
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            role="status"
                            className="text-small text-default-500"
                        >
                            This one is taking a while. You can work elsewhere in Studio; the answer
                            will be in this conversation when it&rsquo;s ready.
                        </motion.span>
                    )}
                </div>
                {isInternal && thinkingText && (
                    <Button
                        isIconOnly
                        size="sm"
                        variant="light"
                        className="h-6 w-6 min-w-0"
                        onPress={() => setShowThinking((prev) => !prev)}
                        aria-label="Toggle thinking details"
                        aria-expanded={showThinking}
                        aria-controls={thinkingPanelId}
                    >
                        <Icon
                            icon={showThinking ? 'lucide:chevron-up' : 'lucide:chevron-down'}
                            width={14}
                            height={14}
                        />
                    </Button>
                )}
            </div>
            <AnimatePresence>
                {showThinking && thinkingText && (
                    <motion.div
                        id={thinkingPanelId}
                        role="region"
                        aria-label="Thinking details"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                    >
                        <div
                            ref={thinkingScrollRef}
                            className="mt-2 max-h-80 overflow-auto rounded-lg border border-default-200 p-4"
                        >
                            <div className="prose prose-sm dark:prose-invert max-w-none leading-relaxed text-default-600">
                                <Markdown components={thinkingMarkdownComponents}>
                                    {thinkingText}
                                </Markdown>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};
